/**
 * Sync `template_components.card_image` from the first feed item — same path as
 * link-in-bio loaders calling {@link handleFirstImage} with `from: website-template`.
 */

import { supabase } from "../../../config/supabaseClient";
import {
  getFirstUsableImageUrlForCard,
  handleFirstImage,
  pickFirstNonEmptyImageField,
} from "../../../Utils/StayUpToDate/RenderComponent";
import {
  TEMPLATE_COMPONENTS_TABLE,
  WEBSITE_TEMPLATE_SURFACE,
} from "../../../Utils/StayUpToDate/componentSurface";
import { fetchFirstTemplateTabFeedItem } from "./fetchFirstTemplateTabFeedItem";

const TEMPLATE_ROW_SELECT =
  "id,name,user_id,unique_id,card_image,is_card_image_updated,type,display_name,post_ids,query,custom_order,content_order,details,shoppable_content_filter,removed_postids,pinned_posts,shoppable_status_filter";

function normalizeCardImageUrl(url: string): string {
  const trimmed = url.trim();
  if (trimmed.startsWith("http://")) {
    return `https://${trimmed.slice("http://".length)}`;
  }
  return trimmed;
}

async function copyCardImageFromStayUpToDate(
  userId: string,
  uniqueId: string,
): Promise<string | null> {
  const uid = uniqueId.trim();
  if (!userId.trim() || !uid) return null;
  const { data, error } = await supabase
    .from("stayuptodate_components")
    .select("card_image")
    .eq("user_id", userId)
    .eq("unique_id", uid)
    .neq("status", "deleted")
    .limit(1);
  if (error || !data?.length) return null;
  return pickFirstNonEmptyImageField(
    (data[0] as { card_image?: unknown }).card_image,
  );
}

/**
 * When `is_card_image_updated` is not true, fill `template_components.card_image`
 * from the matching Stay Up To Date cover (fork often omitted it) or the first
 * feed tile — same path loaders use after a tab is opened.
 */
export async function syncTemplateComponentCardImageIfNeeded(
  userId: string,
  username: string,
  componentId: string,
): Promise<string | null> {
  const uid = userId.trim();
  const cid = componentId.trim();
  if (!uid || !cid) return null;

  const { data: row, error } = await supabase
    .from(TEMPLATE_COMPONENTS_TABLE)
    .select(TEMPLATE_ROW_SELECT)
    .eq("id", cid)
    .eq("user_id", uid)
    .neq("status", "deleted")
    .maybeSingle();

  if (error || !row) return null;

  const existing = pickFirstNonEmptyImageField(row.card_image);
  if (existing && row.is_card_image_updated === true) return existing;

  const uniqueId = String(
    (row as { unique_id?: unknown }).unique_id ?? "",
  ).trim();
  if (!existing && uniqueId) {
    const fromLib = await copyCardImageFromStayUpToDate(uid, uniqueId);
    if (fromLib) {
      await supabase
        .from(TEMPLATE_COMPONENTS_TABLE)
        .update({ card_image: fromLib, isEmpty: false })
        .eq("id", cid)
        .eq("user_id", uid);
      return fromLib;
    }
  }

  const firstItem = await fetchFirstTemplateTabFeedItem(uid, username, row);
  let fromFeed = "";
  if (firstItem) {
    fromFeed = normalizeCardImageUrl(
      String(getFirstUsableImageUrlForCard(firstItem) || "")
        .split(",")[0]
        ?.trim() || "",
    );
    const name = String(row.name ?? "").trim();
    await handleFirstImage(
      name,
      row.id,
      firstItem,
      uid,
      WEBSITE_TEMPLATE_SURFACE,
    );
  } else if (!existing) {
    return null;
  }

  const { data: refreshed } = await supabase
    .from(TEMPLATE_COMPONENTS_TABLE)
    .select("card_image,is_card_image_updated")
    .eq("id", cid)
    .eq("user_id", uid)
    .maybeSingle();

  return (
    pickFirstNonEmptyImageField(refreshed?.card_image) ||
    fromFeed ||
    existing ||
    null
  );
}

/** Fire-and-forget: sync all About More From rows as soon as template data loads. */
export function syncAboutMoreFromTemplateCardImagesInBackground(
  userId: string,
  username: string,
  aboutValues: { stayComponentRowId?: string }[],
): void {
  const uid = userId.trim();
  const uname = username.trim().toLowerCase();
  if (!uid || !uname) return;

  const ids = aboutValues
    .map((v) => v.stayComponentRowId?.trim())
    .filter((id): id is string => Boolean(id));

  if (ids.length === 0) return;

  void Promise.allSettled(
    ids.map((id) => syncTemplateComponentCardImageIfNeeded(uid, uname, id)),
  );
}
